import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import config from 'constants/config'
import { getAbnChecksumStatus, normaliseAbn } from 'businessTraveller/utils/abnUtils'
import requestGetAbnDetails from 'businessTraveller/api/requestGetAbnDetails'
import { createDatadogLoggerWithContext } from 'services/datadogRum'

export type AbnValidationStatus = 'pending' | 'checking' | 'valid' | 'invalid'

export const ABN_INVALID_MESSAGE = 'Invalid ABN'
export const ABN_VALID_MESSAGE = 'Valid ABN'
export const ABN_FORMAT_MESSAGE = 'Please enter an 11 digit ABN'

// 60 minutes
const ABN_LOOKUP_STALE_TIME = 60 * 60 * 1000

const logAbnLookup = createDatadogLoggerWithContext({ feature: 'business-signup-abn-validation' })

function getAbnInvalidMessage(input: HTMLInputElement) {
  if (input.validity.patternMismatch || input.validity.tooShort) {
    return ABN_FORMAT_MESSAGE
  }
  return undefined
}

function useAbnLookup(abn: string, enabled: boolean) {
  return useQuery<boolean, Error>({
    queryKey: ['abr', 'abn-details', normaliseAbn(abn)],
    queryFn: () => requestGetAbnDetails(normaliseAbn(abn)),
    enabled,
    staleTime: ABN_LOOKUP_STALE_TIME,
    retry: 1,
  })
}

function useAbnValidation(initialAbn?: string) {
  const [abn, setAbn] = useState<string>(initialAbn ?? '')

  const checksumStatus = useMemo(() => getAbnChecksumStatus(abn), [abn])

  // Only hit the ABR once the checksum passes.
  const lookupEnabled = checksumStatus === 'valid' && !!config.ABR_GUID
  const lookup = useAbnLookup(abn, lookupEnabled)

  useEffect(() => {
    if (lookup.error) {
      logAbnLookup('ABR lookup failed, falling back to checksum only', 'warn', { abn: normaliseAbn(abn), errorMessage: lookup.error.message })
    }
  }, [abn, lookup.error])

  const status = useMemo<AbnValidationStatus>(() => {
    if (checksumStatus !== 'valid') {
      return checksumStatus
    }
    // Fail open when the ABR is unavailable or not configured, so a third party never blocks signups
    if (!lookupEnabled || lookup.isError) {
      return 'valid'
    }
    if (lookup.data !== undefined) {
      return lookup.data ? 'valid' : 'invalid'
    }
    return 'checking'
  }, [checksumStatus, lookup.data, lookup.isError, lookupEnabled])

  const handleAbnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAbn(e.target.value)
  }, [])

  return {
    abn,
    status,
    isValid: status === 'valid',
    handleAbnChange,
    errorMessage: status === 'invalid' ? ABN_INVALID_MESSAGE : undefined,
    successMessage: status === 'valid' ? ABN_VALID_MESSAGE : undefined,
    getInvalidMessage: getAbnInvalidMessage,
  }
}

export default useAbnValidation
